import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Eye, EyeOff, Construction, Save, AlertTriangle, Search } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

// Mirrors AdminSEO PAGES roughly. One source of truth could be extracted later.
const PAGES = [
  { key: 'home', label: 'Anasayfa', path: '/', group: 'Genel' },
  { key: 'about', label: 'Hakkımızda / Kurumsal', path: '/kurumsal', group: 'Kurumsal' },
  { key: 'presidents', label: 'Başkanlarımız', path: '/kurumsal/baskanlar', group: 'Kurumsal' },
  { key: 'boards', label: 'Kurullarımız', path: '/kurumsal/kurullar', group: 'Kurumsal' },
  { key: 'media', label: 'Medyada YEKÜV', path: '/kurumsal/medya', group: 'Kurumsal' },
  { key: 'units', label: 'Eğitim Destek Birimleri', path: '/kurumsal/birimler', group: 'Kurumsal' },
  { key: 'sponsors', label: 'Destekçilerimiz', path: '/kurumsal/destekciler', group: 'Kurumsal' },
  { key: 'school', label: 'Nilüfer Gökay Okulu', path: '/kurumsal/okul', group: 'Kurumsal' },
  { key: 'tax', label: 'Vergi Muafiyeti', path: '/kurumsal/vergi', group: 'Kurumsal' },
  { key: 'kvkk', label: 'KVKK', path: '/kurumsal/kvkk', group: 'Kurumsal' },
  { key: 'donate', label: 'Bağış (Online)', path: '/bagis', group: 'Bağış' },
  { key: 'donation_types', label: 'Bağış Türleri', path: '/bagis/turler', group: 'Bağış' },
  { key: 'special_day', label: 'Özel Gün Bağışı', path: '/bagis/ozel-gun', group: 'Bağış' },
  { key: 'donation_channels', label: 'Bağış Kanalları', path: '/bagis/kanallar', group: 'Bağış' },
  { key: 'scholarships', label: 'Burslar', path: '/burslar', group: 'Burs' },
  { key: 'projects', label: 'Projeler & Etkinlikler', path: '/projeler', group: 'Projeler' },
  { key: 'news', label: 'Duyurular', path: '/duyurular', group: 'İletişim' },
  { key: 'contact', label: 'İletişim', path: '/iletisim', group: 'İletişim' },
  { key: 'volunteer', label: 'Gönüllülük', path: '/gonullu', group: 'İletişim' },
];

const STATUS_META = {
  published: { label: 'Yayında', icon: Eye, dot: 'bg-emerald-500', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  draft:     { label: 'Taslak',  icon: EyeOff, dot: 'bg-coral-300', cls: 'bg-coral-50 text-coral-300 border-coral-200' },
  maintenance: { label: 'Bakım', icon: Construction, dot: 'bg-gold', cls: 'bg-gold/15 text-ink-700 border-gold/30' },
};

export default function AdminVisibility() {
  const [values, setValues] = useState({});
  const [filter, setFilter] = useState('');
  const [savingKey, setSavingKey] = useState(null);
  const [siteMaintBusy, setSiteMaintBusy] = useState(false);

  const reload = async () => {
    try {
      const list = await api.adminListSettings();
      const m = {};
      list.forEach(s => { m[s.key] = s.value; });
      setValues(m);
    } catch { toast.error('Ayarlar yüklenemedi'); }
  };
  useEffect(() => { reload(); }, []);

  const setStatus = async (slug, next) => {
    setSavingKey(slug);
    try {
      await api.adminUpsertSetting(`page_visibility_${slug}`, next);
      setValues(v => ({ ...v, [`page_visibility_${slug}`]: next }));
      const meta = STATUS_META[next];
      toast.success(`${PAGES.find(p => p.key === slug)?.label}: ${meta?.label || next}`);
    } catch { toast.error('Kaydedilemedi'); }
    finally { setSavingKey(null); }
  };

  const debounceRefs = useRef({});
  const updateMaintText = (slug, kind, val) => {
    const key = `page_${kind}_${slug}`;
    setValues(v => ({ ...v, [key]: val }));
    if (debounceRefs.current[key]) clearTimeout(debounceRefs.current[key]);
    debounceRefs.current[key] = setTimeout(() => {
      api.adminUpsertSetting(key, val).catch(() => {});
    }, 500);
  };

  const toggleSiteMaint = async () => {
    setSiteMaintBusy(true);
    const next = (values.site_maintenance_mode === '1' ? '' : '1');
    try {
      await api.adminUpsertSetting('site_maintenance_mode', next);
      setValues(v => ({ ...v, site_maintenance_mode: next }));
      toast.success(next === '1' ? 'Site bakım moduna alındı' : 'Site bakım modu kapatıldı');
    } catch { toast.error('Kaydedilemedi'); }
    finally { setSiteMaintBusy(false); }
  };

  const grouped = useMemo(() => {
    const filt = filter.toLowerCase();
    return PAGES
      .filter(p => !filt || p.label.toLowerCase().includes(filt) || p.path.includes(filt))
      .reduce((acc, p) => { (acc[p.group] = acc[p.group] || []).push(p); return acc; }, {});
  }, [filter]);

  const counts = PAGES.reduce((acc, p) => {
    const s = values[`page_visibility_${p.key}`] || 'published';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const siteMaintActive = values.site_maintenance_mode === '1';

  return (
    <div data-testid="admin-visibility">
      <div className="mb-6">
        <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">Yayın Yönetimi</div>
        <h1 className="font-display text-4xl mt-2 leading-none">Sayfa Görünürlük Kontrolü</h1>
        <p className="mt-2 text-ink-400 text-sm max-w-3xl">
          Her sayfanın yayın durumunu buradan yönetin: <strong>Yayında</strong> herkese açık, <strong>Taslak</strong> ziyaretçiye "yapım aşamasında" kartı gösterir, <strong>Bakım</strong> ise özelleştirilebilir bir bakım mesajı gösterir.
          Admin olarak giriş yaptıysanız taslak ve bakım sayfalarını <code className="text-xs bg-cream-200 px-1 rounded">?preview=1</code> ekleyerek görebilirsiniz.
        </p>
      </div>

      {/* Site-wide maintenance switch */}
      <div className={`rounded-2xl border p-5 mb-6 flex flex-wrap items-center gap-4 ${siteMaintActive ? 'bg-gold/10 border-gold/40' : 'bg-white border-ink-100'}`}>
        <div className={`w-12 h-12 rounded-xl grid place-items-center shrink-0 ${siteMaintActive ? 'bg-gold text-ink-700' : 'bg-ink-50 text-ink-400'}`}>
          <AlertTriangle className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="font-display text-xl text-ink-700">Site Geneli Bakım Modu</div>
          <div className="text-sm text-ink-500 mt-1">Tüm public sayfaların ziyaretçilere kapatılır, "bakım modunda" mesajı gösterilir. Adminler etkilenmez.</div>
        </div>
        <button
          onClick={toggleSiteMaint}
          disabled={siteMaintBusy}
          data-testid="toggle-site-maintenance"
          className={`px-6 h-12 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60 ${siteMaintActive ? 'bg-coral-300 text-cream hover:bg-coral-400' : 'bg-ink-600 text-cream hover:bg-ink-500'}`}
        >
          {siteMaintActive ? 'Bakım Modunu Kapat' : 'Bakım Moduna Al'}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {['published', 'draft', 'maintenance'].map(s => {
          const meta = STATUS_META[s];
          return (
            <div key={s} className={`rounded-xl border p-4 ${meta.cls}`}>
              <div className="flex items-center gap-2">
                <meta.icon className="w-4 h-4" />
                <div className="text-[10px] tracking-[0.25em] uppercase font-bold">{meta.label}</div>
              </div>
              <div className="font-display text-3xl mt-2 tabular">{counts[s] || 0}</div>
            </div>
          );
        })}
      </div>

      {/* Filter */}
      <div className="relative mb-4 max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
        <input
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Sayfa ara..."
          className="w-full h-10 pl-9 pr-3 rounded-full bg-white border border-ink-100 focus:border-ink-500 outline-none text-sm"
        />
      </div>

      {/* Pages */}
      <div className="space-y-6">
        {Object.entries(grouped).map(([group, pages]) => (
          <div key={group} className="bg-white rounded-2xl border border-ink-100 overflow-hidden">
            <div className="px-5 py-3 border-b border-ink-100 bg-cream-100 text-[10px] tracking-[0.3em] uppercase text-ink-500 font-bold">{group}</div>
            <div className="divide-y divide-ink-100">
              {pages.map(p => {
                const cur = values[`page_visibility_${p.key}`] || 'published';
                const meta = STATUS_META[cur];
                return (
                  <div key={p.key} className="px-5 py-4" data-testid={`vis-row-${p.key}`}>
                    <div className="flex flex-wrap items-center gap-4">
                      <div className={`w-2.5 h-2.5 rounded-full ${meta.dot} shrink-0`} />
                      <div className="flex-1 min-w-[180px]">
                        <div className="font-display text-lg text-ink-700">{p.label}</div>
                        <a href={p.path + (cur !== 'published' ? '?preview=1' : '')} target="_blank" rel="noopener noreferrer" className="text-xs text-ink-400 hover:text-ink-600 font-mono">{p.path}</a>
                      </div>
                      <div className="flex items-center gap-1 bg-cream-200 rounded-full p-1" role="tablist">
                        {['published', 'draft', 'maintenance'].map(s => {
                          const M = STATUS_META[s];
                          const active = cur === s;
                          return (
                            <button
                              key={s}
                              onClick={() => setStatus(p.key, s)}
                              disabled={savingKey === p.key}
                              data-testid={`set-${p.key}-${s}`}
                              className={`px-3 h-9 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${active ? 'bg-ink-600 text-cream' : 'text-ink-500 hover:bg-white'}`}
                            >
                              <M.icon className="w-3.5 h-3.5" /> {M.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {cur === 'maintenance' && (
                      <div className="mt-4 ml-7 grid sm:grid-cols-2 gap-3 max-w-3xl">
                        <input
                          value={values[`page_maintenance_title_${p.key}`] || ''}
                          onChange={e => updateMaintText(p.key, 'maintenance_title', e.target.value)}
                          placeholder="Bakım başlığı (opsiyonel) — boşsa varsayılan"
                          className="bg-cream-200 border border-ink-100 focus:border-ink-500 rounded-xl px-3 h-10 text-sm outline-none"
                        />
                        <input
                          value={values[`page_maintenance_text_${p.key}`] || ''}
                          onChange={e => updateMaintText(p.key, 'maintenance_text', e.target.value)}
                          placeholder="Bakım açıklaması (opsiyonel)"
                          className="bg-cream-200 border border-ink-100 focus:border-ink-500 rounded-xl px-3 h-10 text-sm outline-none"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
